import { LoadingIndicator } from "@/Icons/loadingIndicator";
import { useTerminalsState } from "@/States/Terminals";
import { useCommonEntitiesStore } from "@/States/commonEntities";
import { formatWeight } from "@/Utils/formatFunctions";
import { getUniqKeys } from "@/Utils/getUniqKeys";
import { trpc } from "@/Utils/trpc";
import { timezoneStore, useConfig } from "@/api/hooks/useConfig";
import { EmptyView } from "@/components/EmptyView/EmptyView";
import { Card, CardComponentFilterLocation } from "@/components/pageCards/card";
import { ActiveOptions } from "@/components/pageCards/filterSort/FilterSortContext";
import { createTimeFilters } from "@/components/pageCards/filterSort/filterCreators";
import { getTimeFrame } from "@/components/pageCards/filterSort/filterTimeFrame";
import { Granularity } from "@/components/pageCards/filterSort/periodicity";
import { FilterSort } from "@/components/pageCards/filterSort/types";
import { useConfigService } from "@/pages/configuration/useConfigService";
import {
  COLORS,
  getNewColor,
} from "@/pages/customer/dashboard/functions/pieChartData";
import { useHomeLoadingState } from "@/pages/home/loadingState";
import { usePdfPreview } from "@/pages/home/pdfPreview";
import { useTrans } from "@/translations";

import React, { useCallback, useEffect, useMemo, useState } from "react";

import { ResponsiveBar } from "@nivo/bar";
import { capitalize, uniq } from "lodash";
import omit from "lodash/omit";
import moment from "moment-timezone";
import {
  DashboardTileType,
  FilterTimeFrameValue,
} from "server/config-api/index.js";
import { QuantityUnit, WasteType } from "server/dwhApi/common.js";
import { Terminal, TerminalPeriod } from "server/dwhApi/terminals/types.js";

import { DevelopmentInWasteTypesDataInterface } from "./common";

type Props = {
  timeFilter?: FilterTimeFrameValue;
  setTimeFilters?: (
    value: FilterTimeFrameValue,
    type: DashboardTileType,
  ) => void;
};

export const DevelopmentInWasteTypesRE: React.FC<Props> = ({
  timeFilter,
  setTimeFilters,
}) => {
  const [filters, setFilters] = useState<string[]>([]);
  const { t, language } = useTrans();
  const { wasteTypes } = useCommonEntitiesStore();
  const { currentTerminal } = useTerminalsState();
  const { config } = useConfig();
  const { pdfPreviewOpen } = usePdfPreview();
  const { timezone } = timezoneStore();

  const { wasteTypeConfig, terminalConfig } = useConfigService();
  const { setDevelopmentLoading } = useHomeLoadingState();

  const [{ period: [selectedPeriod] = [] }, setActiveOptions] = useState<
    ActiveOptions<"period">
  >({} as any);

  const defaultPeriod = useMemo(
    () =>
      timeFilter ||
      terminalConfig?.defaultTimeFrame ||
      FilterTimeFrameValue.Last6FullMonths,
    [terminalConfig, timeFilter],
  );

  const timeFilters: FilterSort[] = useMemo(
    () => [
      createTimeFilters({ defaultValue: defaultPeriod }, [
        FilterTimeFrameValue.Last6FullMonths,
        FilterTimeFrameValue.LastFullYear,
      ]),
    ],
    [defaultPeriod],
  );

  const periodFilterValue = useMemo(
    () =>
      getTimeFrame(
        selectedPeriod?.value as FilterTimeFrameValue,
        defaultPeriod,
      ),
    [selectedPeriod, defaultPeriod],
  );

  const {
    data: periodData,
    isError: isPeriodError,
    isLoading: isPeriodLoading,
  } = trpc.dwh.terminals.getPeriods.useQuery(
    {
      ...periodFilterValue,
      timeZone: timezone,
      terminalId: currentTerminal?.id || "",
      granularity: Granularity.MONTH,
    },
    {
      queryKeyHashFn: () =>
        [
          "terminalPeriods",
          periodFilterValue.startTimeISO,
          periodFilterValue.endTimeISO,
          timezone,
          currentTerminal?.id,
        ].toString(),
      enabled: !!currentTerminal,
    },
  );

  const currentMonthTimeValue = useMemo(
    () => ({
      startTimeISO: moment().tz(timezone, true).startOf("month").toISOString(),
      endTimeISO: moment().tz(timezone, true).endOf("day").toISOString(),
    }),
    [timezone],
  );
  const {
    data: currentMonthData,
    isLoading: isCurrentMonthLoading,
    isError: isCurrentMonthError,
  } = trpc.dwh.terminals.getById.useQuery(
    {
      startTimeISO: currentMonthTimeValue.startTimeISO,
      endTimeISO: currentMonthTimeValue.endTimeISO,
      terminalId: currentTerminal?.id || "",
    },
    {
      queryKeyHashFn: () =>
        [
          "terminal",
          currentTerminal,
          currentMonthTimeValue.startTimeISO,
          currentMonthTimeValue.endTimeISO,
          currentTerminal?.id,
        ].toString(),
      enabled: !!currentTerminal,
    },
  );

  useEffect(() => {
    setDevelopmentLoading(isPeriodLoading || isCurrentMonthLoading);
  }, [setDevelopmentLoading, isPeriodLoading, isCurrentMonthLoading]);

  const getBarData = useCallback(
    (terminal: Terminal, startTime?: string) => {
      const parsedWasteTypes = terminal?.wasteTypes?.reduce(
        (
          prev: any,
          { share, code: wasteCode, weight = {} as QuantityUnit }: WasteType,
        ) => {
          if (share < 0.05)
            return { ...prev, other: prev.other + weight.quantity };
          const wasteType = wasteTypes?.find((w) => w.id === wasteCode);

          const customName = wasteTypeConfig?.find(
            (wtc) => wtc.wasteTypeCode === wasteCode,
          )?.name;
          const wasteTypeName = customName || wasteType?.name || wasteCode;
          return { ...prev, [wasteTypeName]: weight.quantity };
        },
        { other: 0 },
      );

      const { other = "", ...rest } = parsedWasteTypes || {};
      const month = capitalize(
        moment(startTime).tz(timezone).locale(language).format("MMM"),
      );
      return { month, ...rest, other };
    },
    [wasteTypes, language, timezone, wasteTypeConfig],
  );

  const data: DevelopmentInWasteTypesDataInterface[] = useMemo(() => {
    if (!periodData) return [];

    const olderMonthsData = periodData.periods?.map((period: TerminalPeriod) =>
      getBarData(period.terminal, period.startTime),
    );

    const data = olderMonthsData;

    if (
      currentMonthData?.terminal.totalOccurrences &&
      currentMonthData?.terminal.totalOccurrences > 0
    ) {
      const maxNrOfMonths =
        selectedPeriod?.value === FilterTimeFrameValue.LastFullYear ? 12 : 6;

      if (data.length === maxNrOfMonths) {
        data.shift();
      }

      const currentMonthBarData = getBarData(currentMonthData.terminal);
      data.push(currentMonthBarData);
    }

    return (
      data.map(({ other, ...rest }: any) => ({
        ...rest,
        [t("circularityLabels:otherWasteTypes")]: other,
      })) ?? []
    );
  }, [periodData, t, currentMonthData, getBarData, selectedPeriod]);

  const wasteCodes = useMemo(() => {
    if (!periodData) return [];

    const types = uniq(
      periodData.periods?.flatMap(({ terminal }: { terminal: Terminal }) => {
        return terminal.wasteTypes?.map(({ code }: WasteType) => code) ?? [];
      }),
    );

    return types.map((wasteCode: string) => ({
      id: wasteCode,
      name: wasteTypes?.find((w) => w.id === wasteCode)?.name,
    }));
  }, [periodData, wasteTypes]);

  let curColors = [...COLORS];

  const keysWithColor = getUniqKeys(data)
    .filter((item) => item !== "month")
    .map((el) => {
      const [color, newColors] = getNewColor(curColors);
      curColors = newColors;

      if (el === t("circularityLabels:otherWasteTypes"))
        return [el, terminalConfig?.otherColor || config.otherColor || color];

      const wasteCode = wasteCodes?.find((w) => w.name === el)?.id;
      const customColor = wasteTypeConfig?.find(
        (wtc) => wtc.name === el || wtc.wasteTypeCode === wasteCode,
      )?.color;
      return [el, customColor || color];
    });

  const filteredData = data.map((singleBarData) =>
    omit(singleBarData, filters),
  );

  const chart = useMemo(() => {
    if (isPeriodLoading || isCurrentMonthLoading) {
      return (
        <div className="flex h-full min-h-full items-center justify-center">
          <LoadingIndicator />
        </div>
      );
    }

    if (isPeriodError || isCurrentMonthError) {
      return <EmptyView type="failToFetch" className="min-h-[300px]" />;
    }

    if (!periodData?.metadata.eventsExist) {
      return <EmptyView type="noData" className="min-h-[300px]" />;
    } else if (data.length === 0) {
      return (
        <EmptyView
          type="noDataCalculationPeriodUncomplete"
          className="min-h-[300px]"
        />
      );
    }

    return (
      <div className="grid w-full grid-cols-1 items-start gap-2 sm:grid-cols-2">
        <div className="h-[300px] w-full sm:h-[332px]">
          <ResponsiveBar
            data={filteredData}
            keys={keysWithColor.map((el) => el[0]).reverse()}
            indexBy="month"
            isInteractive={true}
            enableGridY={false}
            enableGridX={false}
            axisLeft={null}
            margin={{ top: 0, right: 0, bottom: 25, left: 0 }}
            innerPadding={1}
            labelSkipHeight={10}
            axisBottom={{
              tickSize: 0,
              tickPadding: 8,
              tickRotation: 0,
            }}
            enableLabel={false}
            valueFormat={(value) => formatWeight(value, { suffix: " kg" })}
            colors={({ id }) => {
              const [, color] = keysWithColor.find((el) => el[0] === id) ?? [];
              return color ?? "#ccc";
            }}
          />
        </div>
        <div className="w-full">
          {!pdfPreviewOpen && (
            <h6 className="font-dm-sans mb-2.5 font-semibold text-black">
              {t("hints:clickToSelectOrUnselectWasteTypes")}
            </h6>
          )}
          <ul className="grid grid-cols-1 gap-2 sm:gap-0">
            {keysWithColor.map(([key, color]) => (
              <li key={key} className="mb-2.5 flex items-center last:mb-0">
                <div className="relative h-4 w-4">
                  {pdfPreviewOpen ? (
                    <div
                      className="absolute h-4 w-4 rounded-full"
                      style={{ backgroundColor: color }}
                    />
                  ) : (
                    <input
                      id={`developmentInWasteTypes-${key}`}
                      type="checkbox"
                      data-html2canvas-ignore="true"
                      className="absolute h-4 w-4 rounded-none text-red-600 accent-black"
                      style={{ accentColor: color }}
                      onChange={() =>
                        setFilters((prevState) =>
                          prevState.includes(key)
                            ? prevState.filter((option) => option !== key)
                            : [...prevState, key],
                        )
                      }
                      defaultChecked
                      data-1p-ignore
                    />
                  )}
                </div>
                <label
                  htmlFor={`developmentInWasteTypes-${key}`}
                  className="ml-2 text-xs"
                >
                  {key}
                </label>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  }, [
    filteredData,
    isPeriodError,
    isCurrentMonthError,
    isPeriodLoading,
    isCurrentMonthLoading,
    keysWithColor,
    t,
    data.length,
    pdfPreviewOpen,
    periodData,
  ]);

  const onFilterOptionChange = useCallback(
    (activeOptions: ActiveOptions<any>) => {
      setActiveOptions(activeOptions);

      if (setTimeFilters && activeOptions.period) {
        setTimeFilters(
          activeOptions.period[0].value as FilterTimeFrameValue,
          DashboardTileType.DevelopmentInWasteTypes,
        );
      }
    },
    [setActiveOptions, setTimeFilters],
  );

  return (
    <Card
      title={"circularityLabels:developmentInWasteTypes"}
      filters={timeFilters}
      onFilterOptionChange={onFilterOptionChange}
      filterLocation={CardComponentFilterLocation.RIGHT}
    >
      {chart}
    </Card>
  );
};
